import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/seo/SEOHead';
import { projects } from '../../mocks/projects';
import { getProjectSchema, getBreadcrumbSchema } from '../../utils/structuredData';
import { createSlug, getFullUrl, getImageUrl } from '../../utils/seo';

export default function ProjeDetay() {
  const { id } = useParams<{ id: string }>();
  const project = projects.find(p => p.id === parseInt(id || '0'));
  const allImages = project ? [project.image, ...(project.images || [])] : [];
  const [selectedImage, setSelectedImage] = useState(allImages[0] || '');

  if (!project) {
    return (
      <div className="bg-[#1c1612] min-h-screen flex items-center justify-center">
        <SEOHead
          title="Proje Bulunamadı | Studio Asım Mete"
          description="Aradığınız proje bulunamadı."
          canonical="/projeler"
          noindex={true}
        />
        <div className="text-center">
          <h1 className="text-4xl font-light text-white mb-4 font-serif">Proje Bulunamadı</h1>
          <Link to="/projeler" className="text-white hover:text-white transition-colors duration-300 font-sans">
            Projelere Dön
          </Link>
        </div>
      </div>
    );
  }

  const projectSlug = createSlug(project.title);
  const canonicalUrl = `/proje/${project.id}/${projectSlug}`;
  const projectImage = project.image.startsWith('http') ? project.image : getFullUrl(project.image);
  
  const breadcrumbItems = [
    { name: 'Anasayfa', url: '/' },
    { name: 'Projeler', url: '/projeler' },
    { name: project.title, url: `/proje/${project.id}` }
  ];

  const structuredData = [
    getProjectSchema(project),
    getBreadcrumbSchema(breadcrumbItems)
  ];

  return (
    <div className="bg-[#1c1612] min-h-screen">
      <SEOHead
        title={`${project.title} | ${project.category === 'konut' ? 'Konut' : project.category === 'ofis' ? 'Ofis' : 'Ticari'} Projesi | Studio Asım Mete`}
        description={`${project.title} - ${project.description.substring(0, 155)}...`}
        keywords={`${project.title}, ${project.category}, ${project.location}, iç mimari, ${project.year}`}
        canonical={canonicalUrl}
        ogImage={projectImage}
        ogType="article"
        articlePublishedTime={`${project.year}-01-01T00:00:00+03:00`}
        structuredData={structuredData}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden -mt-20 z-0">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${getImageUrl(project.image)}')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#0e0c0a]/80 via-[#0e0c0a]/70 to-[#0e0c0a]/80"></div>
        </div>

        <div className="relative z-[60] text-center px-6 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-light text-white tracking-wider mb-6 font-serif">
            {project.title}
          </h1>
          <div className="flex flex-wrap justify-center gap-6 text-white font-light text-sm tracking-wide font-sans">
            <span>{project.location}</span>
            <span className="text-white/50">•</span>
            <span>{project.year}</span>
            <span className="text-white/50">•</span>
            <span className="uppercase">{project.category}</span>
          </div>
        </div>
      </section>

      {/* Image Gallery Section - Product Detail Style */}
      {allImages.length > 0 && (
        <section className="py-20 px-6 bg-[#0e0c0a]">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
              {/* Thumbnail Gallery - Left Side */}
              <div className="md:col-span-2 flex md:flex-col gap-4 overflow-x-auto md:overflow-x-visible pb-4 md:pb-0">
                {allImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(image)}
                    className={`flex-shrink-0 relative w-20 h-20 md:w-full md:h-24 overflow-hidden rounded-sm border-2 transition-all duration-300 ${
                      selectedImage === image
                        ? 'border-white'
                        : 'border-white/20 hover:border-white/50'
                    }`}
                  >
                    <img
                      src={getImageUrl(image)}
                      alt={`${project.title} - Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>

              {/* Main Image - Center */}
              <div className="md:col-span-7 relative h-[544px] overflow-hidden rounded-sm group bg-[#0e0c0a]">
                <img
                  src={getImageUrl(selectedImage)}
                  alt={project.title}
                  className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${
                    project.id === 5 || project.id === 6 ? 'object-center' : 'object-left-bottom'
                  }`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0e0c0a]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>

              {/* Description - Right Side */}
              <div className="md:col-span-3 flex items-center">
                <div className="space-y-6">
                  {project.description && (
                    <div>
                      <div className="text-[10px] tracking-[0.4em] text-white/60 uppercase font-sans mb-4">
                        Proje Hakkında
                      </div>
                      <div className="text-white font-light text-base leading-relaxed font-serif tracking-wider space-y-4">
                        {project.description.split('\n\n').map((paragraph, index) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="pt-6 border-t border-white/10">
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-white/60 font-sans">Konum</span>
                        <span className="text-white font-serif">{project.location}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/60 font-sans">Yıl</span>
                        <span className="text-white font-serif">{project.year}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/60 font-sans">Kategori</span>
                        <span className="text-white font-serif uppercase">{project.category}</span>
                      </div>
                      {(project as any).status && (
                        <div className="flex justify-between">
                          <span className="text-white/60 font-sans">Durum</span>
                          <span className="text-white font-serif">{(project as any).status}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Back to Projects - Minimal */}
      <section className="py-32 px-6 bg-[#0e0c0a]">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center gap-8">
            <div className="flex-1 h-px bg-white/20"></div>
            <Link
              to="/projeler"
              className="text-white font-light text-sm tracking-wider uppercase hover:text-white transition-colors duration-300 cursor-pointer font-sans whitespace-nowrap"
            >
              Tüm Projelere Dön
            </Link>
            <div className="flex-1 h-px bg-white/20"></div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
